"use client";

import { c as _c } from "react/compiler-runtime";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { type ComponentType, useEffect, useRef } from "react";
import { useHoverDirty } from "react-use";
import ChatBubbleIcon from "@/components/common/icons/ChatBubbleIcon";
import WavesIcon from "@/components/common/icons/WavesIcon";
import { NEXTGEN_CHAIN_ID, NEXTGEN_CORE } from "@/components/nextGen/nextgen_contracts";
import { USER_PAGE_TAB_IDS } from "@/components/user/layout/userTabs.config";
import { GRADIENT_CONTRACT, MEMELAB_CONTRACT, MEMES_CONTRACT } from "@/constants/constants";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiWave } from "@/generated/models/ApiWave";
import { formatStatFloor, getProfileTargetRoute } from "@/helpers/Helpers";
import { getActiveWaveIdFromUrl, getWaveHomeRoute, getWaveRoute } from "@/helpers/navigation.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import HeaderSearchModalItemMedia from "./HeaderSearchModalItemMedia";
import HeaderSearchModalPfp from "./HeaderSearchModalPfp";
export interface NFTSearchResult {
  id: number;
  name: string;
  contract: string;
  icon_url: string;
  thumbnail_url: string;
  image_url: string;
}
export interface PageSearchResult {
  type: "PAGE";
  title: string;
  href: string;
  breadcrumbs: string[];
  searchTerms?: string[];
  icon?: ComponentType<{
    className?: string | undefined;
  }> | undefined;
}
export type HeaderSearchModalItemType = CommunityMemberMinimal | NFTSearchResult | ApiWave | PageSearchResult;
export const getNftCollectionMap = () => {
  return {
    [MEMES_CONTRACT.toLowerCase()]: {
      title: "The Memes",
      path: "/the-memes"
    },
    [MEMELAB_CONTRACT.toLowerCase()]: {
      title: "Meme Lab",
      path: "/meme-lab"
    },
    [GRADIENT_CONTRACT.toLowerCase()]: {
      title: "6529 Gradient",
      path: "/6529-gradient"
    },
    [NEXTGEN_CORE[NEXTGEN_CHAIN_ID].toLowerCase()]: {
      title: "NextGen",
      path: "/nextgen/token"
    }
  };
};
export default function HeaderSearchModalItem(t0) {
  const $ = _c(89);
  const {
    content,
    isSelected,
    onHover,
    onClose
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const ref = useRef(null);
  const isHovering = useHoverDirty(ref as React.RefObject<HTMLDivElement>);
  const {
    isApp
  } = useDeviceInfo();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover)").matches;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const supportsHover = t1;
  let t2;
  if ($[1] !== content) {
    t2 = () => (content as PageSearchResult).type === "PAGE";
    $[1] = content;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isPage = t2;
  let t3;
  if ($[3] !== content) {
    t3 = () => Object.hasOwn(content, "handle");
    $[3] = content;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isProfile = t3;
  let t4;
  if ($[5] !== content) {
    t4 = () => Object.hasOwn(content, "contract");
    $[5] = content;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isNft = t4;
  let t5;
  if ($[7] !== isNft || $[8] !== isPage || $[9] !== isProfile) {
    t5 = () => !isProfile() && !isNft() && !isPage();
    $[7] = isNft;
    $[8] = isPage;
    $[9] = isProfile;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isWave = t5;
  let t6;
  if ($[11] !== content) {
    t6 = () => content as ApiWave;
    $[11] = content;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const getWave = t6;
  let t7;
  if ($[13] !== content) {
    t7 = () => content as CommunityMemberMinimal;
    $[13] = content;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const getProfile = t7;
  let t8;
  if ($[15] !== content) {
    t8 = () => content as NFTSearchResult;
    $[15] = content;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const getNft = t8;
  let t9;
  if ($[17] !== content) {
    t9 = () => content as PageSearchResult;
    $[17] = content;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const getPage = t9;
  const getMediaIcon = _temp;
  let t10;
  if ($[19] !== getNft || $[20] !== getPage || $[21] !== getProfile || $[22] !== getWave || $[23] !== isNft || $[24] !== isPage || $[25] !== isProfile) {
    t10 = () => {
      if (isProfile()) {
        const profile = getProfile();
        return <HeaderSearchModalPfp level={profile.level} src={profile.pfp} />;
      } else {
        if (isNft()) {
          const nft = getNft();
          return <HeaderSearchModalItemMedia nft={nft} />;
        } else {
          if (isPage()) {
            const page = getPage();
            const Icon_0 = page.icon ?? DocumentTextIcon;
            return getMediaIcon(Icon_0);
          } else {
            const wave = getWave();
            if (wave.picture) {
              return <HeaderSearchModalItemMedia src={wave.picture} alt={wave.name} roundedFull={true} />;
            }
            const isDm = wave.wave.admin_group.group?.is_direct_message;
            if (isDm) {
              return getMediaIcon(ChatBubbleIcon);
            }
            return getMediaIcon(WavesIcon);
          }
        }
      }
    };
    $[19] = getNft;
    $[20] = getPage;
    $[21] = getProfile;
    $[22] = getWave;
    $[23] = isNft;
    $[24] = isPage;
    $[25] = isProfile;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const getMedia = t10;
  let t11;
  if ($[27] !== isHovering || $[28] !== onHover) {
    t11 = () => {
      if (supportsHover) {
        onHover(isHovering);
      }
    };
    $[27] = isHovering;
    $[28] = onHover;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== isHovering) {
    t12 = [isHovering, supportsHover];
    $[30] = isHovering;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  useEffect(t11, t12);
  let t13;
  if ($[32] !== getNft || $[33] !== getPage || $[34] !== getProfile || $[35] !== getWave || $[36] !== isApp || $[37] !== isNft || $[38] !== isPage || $[39] !== isProfile || $[40] !== pathname || $[41] !== searchParams) {
    t13 = () => {
      if (isProfile()) {
        const profile_0 = getProfile();
        return getProfileTargetRoute({
          handleOrWallet: profile_0.handle ?? profile_0.wallet.toLowerCase(),
          pathname: pathname ?? "",
          defaultPath: USER_PAGE_TAB_IDS.REP
        });
      } else {
        if (isNft()) {
          const nft_0 = getNft();
          const collectionMap = getNftCollectionMap();
          const key = nft_0.contract.toLowerCase();
          return `${collectionMap[key]?.path}/${nft_0.id}`;
        } else {
          if (isPage()) {
            return getPage().href;
          } else {
            const wave_0 = getWave();
            const currentWaveId = getActiveWaveIdFromUrl({
              pathname,
              searchParams
            }) ?? undefined;
            const isDirectMessage = wave_0.chat?.scope?.group?.is_direct_message ?? false;
            if (currentWaveId === wave_0.id) {
              return getWaveHomeRoute({
                isDirectMessage,
                isApp
              });
            }
            return getWaveRoute({
              waveId: wave_0.id,
              isDirectMessage,
              isApp
            });
          }
        }
      }
    };
    $[32] = getNft;
    $[33] = getPage;
    $[34] = getProfile;
    $[35] = getWave;
    $[36] = isApp;
    $[37] = isNft;
    $[38] = isPage;
    $[39] = isProfile;
    $[40] = pathname;
    $[41] = searchParams;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const getPath = t13;
  let t14;
  if ($[43] !== getNft || $[44] !== getPage || $[45] !== getProfile || $[46] !== getWave || $[47] !== isNft || $[48] !== isPage || $[49] !== isProfile) {
    t14 = () => {
      if (isProfile()) {
        return getProfile().handle ?? "-";
      } else {
        if (isNft()) {
          return getNft().name;
        } else {
          if (isPage()) {
            return getPage().title;
          } else {
            return getWave().name;
          }
        }
      }
    };
    $[43] = getNft;
    $[44] = getPage;
    $[45] = getProfile;
    $[46] = getWave;
    $[47] = isNft;
    $[48] = isPage;
    $[49] = isProfile;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const getPrimaryText = t14;
  let t15;
  if ($[51] !== getNft || $[52] !== getPage || $[53] !== getProfile || $[54] !== getWave || $[55] !== isNft || $[56] !== isPage || $[57] !== isProfile) {
    t15 = () => {
      if (isProfile()) {
        const profile_1 = getProfile();
        return `TDH: ${formatStatFloor(profile_1.tdh)} - Level: ${profile_1.level}`;
      } else {
        if (isNft()) {
          const nft_1 = getNft();
          const collectionMap_0 = getNftCollectionMap();
          const key_0 = nft_1.contract.toLowerCase();
          return `${collectionMap_0[key_0]?.title} #${nft_1.id}`;
        } else {
          if (isPage()) {
            const page_0 = getPage();
            if (page_0.breadcrumbs.length > 0) {
              return page_0.breadcrumbs.join(" \u2022 ");
            }
            return page_0.href;
          } else {
            const wave_1 = getWave();
            const author = wave_1.author?.handle ?? wave_1.author?.primary_address;
            return author ? `by ${author}` : `Wave #${wave_1.serial_no}`;
          }
        }
      }
    };
    $[51] = getNft;
    $[52] = getPage;
    $[53] = getProfile;
    $[54] = getWave;
    $[55] = isNft;
    $[56] = isPage;
    $[57] = isProfile;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  const getSecondaryText = t15;
  let t16;
  if ($[59] !== isWave) {
    t16 = () => {
      if (isWave()) {
        return "tw-mb-0 tw-min-w-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-xs tw-text-iron-500";
      }
      return "tw-mb-0 tw-min-w-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm tw-text-iron-400";
    };
    $[59] = isWave;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  const getSecondaryTextClassName = t16;
  let t17;
  if ($[61] !== getPrimaryText) {
    t17 = getPrimaryText();
    $[61] = getPrimaryText;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const primaryText = t17;
  let t18;
  if ($[63] !== getSecondaryText) {
    t18 = getSecondaryText();
    $[63] = getSecondaryText;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  const secondaryText = t18;
  const primaryTextClassName = `tw-min-w-0 tw-flex-1 tw-font-semibold tw-text-white ${isWave() ? "tw-text-base" : "tw-text-sm"}`;
  const t19 = `${isSelected ? "tw-bg-iron-800" : ""} tw-my-1 tw-w-full tw-rounded-lg tw-px-2 tw-py-2 tw-transition tw-duration-300 tw-ease-out`;
  let t20;
  if ($[65] !== getPath) {
    t20 = getPath();
    $[65] = getPath;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== getMedia) {
    t21 = getMedia();
    $[67] = getMedia;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== primaryText) {
    t22 = <span className="tw-block tw-min-w-0 tw-truncate">{primaryText}</span>;
    $[69] = primaryText;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== primaryText || $[72] !== primaryTextClassName || $[73] !== t22) {
    t23 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3"><span className={primaryTextClassName} title={primaryText}>{t22}</span></div>;
    $[71] = primaryText;
    $[72] = primaryTextClassName;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== getSecondaryTextClassName || $[76] !== secondaryText) {
    t24 = secondaryText && <p className={getSecondaryTextClassName()} title={secondaryText}>{secondaryText}</p>;
    $[75] = getSecondaryTextClassName;
    $[76] = secondaryText;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== t23 || $[79] !== t24) {
    t25 = <div className="tw-min-w-0 tw-flex-1">{t23}{t24}</div>;
    $[78] = t23;
    $[79] = t24;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== onClose || $[82] !== t20 || $[83] !== t21 || $[84] !== t25) {
    t26 = <Link href={t20} onClick={onClose} className="tw-group tw-flex tw-w-full tw-min-w-0 tw-select-none tw-items-center tw-gap-3 tw-text-left tw-text-sm tw-font-medium tw-no-underline">{t21}{t25}</Link>;
    $[81] = onClose;
    $[82] = t20;
    $[83] = t21;
    $[84] = t25;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t19 || $[87] !== t26) {
    t27 = <div ref={ref} className={t19}>{t26}</div>;
    $[86] = t19;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  return t27;
}
function _temp(Icon) {
  return <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg tw-bg-iron-900"><Icon className="tw-h-5 tw-w-5" /></div>;
}